import StorageKeys from '@/constants/StorageKeys';
import { returnUrlQuery } from '@/helpers/loginHelpers';
import { useAuthStore } from '@/services/stores/authStore';

const loginPath = '/login';

/**
 * Goes to the login page with a full reload, which continues with `returnUrl` (by default the current page) after
 * login. The reload also drops the hub connection, which can't be configured twice, and every module-level cached
 * store, so the next user never sees the previous session's rows.
 */
export function reloadToLogin(returnUrl = currentUrl()): void {
    if (!window.location.pathname.startsWith(loginPath)) {
        const query = new URLSearchParams(returnUrlQuery(returnUrl)).toString();
        window.location.assign(query ? `${loginPath}?${query}` : loginPath);
    }
}

function currentUrl(): string {
    const { pathname, search, hash } = window.location;
    return `${pathname}${search ?? ''}${hash ?? ''}`;
}

/** The session can't be recovered: drop the token and reload to the login page. */
export function endSession(): void {
    localStorage.removeItem(StorageKeys.Token);
    reloadToLogin();
}

/**
 * Keeps this tab in step with the others, which share the token in localStorage: a refresh elsewhere (the same user)
 * only updates the signed-in user; a logout elsewhere reloads to the login page; a login as someone else reloads, so
 * no view, store or hub connection of the previous user stays behind. Registered on the window's 'storage' event,
 * which fires in every tab but the one that made the change.
 */
export function followOtherTabs(event: StorageEvent): void {
    // A null key is localStorage.clear().
    if (event.key !== null && event.key !== StorageKeys.Token) {
        return;
    }

    const auth = useAuthStore();
    const previous = auth.user.username;
    if (previous === '') {
        // Not signed in here: the next navigation reads the token.
        return;
    }

    const token = event.key === null ? null : event.newValue;
    if (!token) {
        reloadToLogin();
        return;
    }

    auth.initialize(token);
    if (auth.user.username !== previous) {
        window.location.reload();
    }
}
